import { debounce, omit } from 'lodash'
import { InputAdornment, Stack, TextField, Box, LinearProgress, Autocomplete } from '@mui/material'
import * as action from 'store/member'
import { resetQuery } from 'store/member'
import { CloseIcon, SearchIcon } from 'components/Icons'
import { useDispatch, useSelector } from 'react-redux'
import { useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { getGradeLevelAndSections, getJobPositions } from 'store/lookup'
import { useTranslation } from 'react-i18next'
import { useSession } from 'components/Providers'

function SearchBar() {
  const dispatch = useDispatch()
  const { employmentType } = useParams()
  const { t } = useTranslation()
  const { searchLoading } = useSelector(state => state.member)
  const query = useSelector(state => state.member.query[employmentType])
  const { gradeLevelAndSections } = useSelector(state => state.lookup)
  const jobPosition = useSelector(state => state.lookup.jobPosition)
  const { academicYear } = useSession()

  const [que, setQue] = useState(query?.que || '')
  const [selectedGrade, setSelectedGrade] = useState(null)
  const [selectedJob, setSelectedJob] = useState(null)
  const [status, setStatus] = useState(null)

  const statusOptions = [
    { label: employmentType === 'student' ? t('Enrolled') : t('Active'), value: true },
    { label: employmentType === 'student' ? t('Unenrolled') : t('Inactive'), value: false },
  ]
  const defaultStatus = statusOptions.find(o => o.value)

  // Reset all filters and re-fetch when employment type changes.
  // Default the status filter to Enrolled (student) / Active (staff).
  useEffect(() => {
    dispatch(resetQuery({ type: employmentType }))
    setQue('')
    setSelectedGrade(null)
    setSelectedJob(null)
    setStatus(defaultStatus)
    dispatch(action.search({ type: employmentType, limit: 10, offset: 0, enrollment: defaultStatus.value }))
  }, [employmentType])

  useEffect(() => {
    jobPosition || dispatch(getJobPositions())
  }, [dispatch, Boolean(jobPosition)])

  useEffect(() => {
    gradeLevelAndSections || (academicYear && dispatch(getGradeLevelAndSections()))
  }, [dispatch, academicYear, Boolean(gradeLevelAndSections)])


  // Flatten grade levels + sections: sections are visually indented via renderOption
  const gradeOptions = useMemo(() =>
    gradeLevelAndSections?.flatMap(el => [
      { label: `Grade ${el.id}`, gradeLevelId: el.id, gradeSectionId: null },
      ...el.section.map(s => ({
        label: `Section ${s.gradeSection}`,
        gradeLevelId: el.id,
        gradeSectionId: s.id,
      })),
    ]) ?? [],
    [gradeLevelAndSections]
  )

  const applyFilter = (key, value) => {
    const commonProperties = { type: employmentType, offset: 0 }

    let updatedQuery
    if (key === 'grade') {
      if (value?.gradeLevelId && value?.gradeSectionId) {
        updatedQuery = { ...query, gradeLevelId: value.gradeLevelId, gradeSectionId: value.gradeSectionId, ...commonProperties }
      } else if (value?.gradeLevelId) {
        updatedQuery = { ...omit(query, 'gradeSectionId'), gradeLevelId: value.gradeLevelId, ...commonProperties }
      } else {
        updatedQuery = { ...omit(query, 'gradeSectionId', 'gradeLevelId'), ...commonProperties }
      }
    } else if (key === 'que') {
      if (typeof value === 'string' && value.trim().length > 2) {
        updatedQuery = { ...query, que: value, ...commonProperties }
      } else {
        updatedQuery = { ...omit(query, 'que'), ...commonProperties }
      }
    } else if (value === null || value === undefined) {
      updatedQuery = { ...omit(query, key), ...commonProperties }
    } else {
      updatedQuery = { ...query, [key]: value, ...commonProperties }
    }

    dispatch(action.search(updatedQuery))
  }

  const delayedSearch = useMemo(() =>
    debounce((q, cb) => cb('que', q), 500),
    []
  )

  return (
    <Box sx={{ position: 'relative' }}>
      <Stack direction="row" spacing={2} sx={{ p: 2 }}>
        <TextField
          size="small"
          fullWidth
          placeholder={t('Search')}
          value={que}
          onChange={e => {
            setQue(e.target.value)
            delayedSearch(e.target.value, applyFilter)
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            endAdornment: (
              <CloseIcon
                onClick={() => { setQue(''); applyFilter('que', '') }}
                sx={{ '&:hover': { cursor: 'pointer' }, fontSize: 'small' }}
              />
            ),
          }}
        />

        {employmentType === 'student' ? (
          <Autocomplete
            sx={{ width: '27em' }}
            size="small"
            options={gradeOptions}
            getOptionLabel={o => o.gradeSectionId ? `Grade ${o.gradeLevelId} — ${o.label}` : o.label}
            isOptionEqualToValue={(o, v) =>
              o.gradeLevelId === v.gradeLevelId && o.gradeSectionId === v.gradeSectionId
            }
            value={selectedGrade}
            onChange={(_, v) => {
              setSelectedGrade(v)
              applyFilter('grade', v)
            }}
            renderOption={({ style, ...props }, option) => (
              <li {...props} style={{ ...style, paddingLeft: option.gradeSectionId ? 40 : 16 }}>
                {option.label}
              </li>
            )}
            renderInput={params => <TextField {...params} label={t('Grade Level / Section')} />}
          />
        ) : (
          <Autocomplete
            sx={{ width: '27em' }}
            size="small"
            options={jobPosition ?? []}
            getOptionLabel={o => o.name ?? ''}
            isOptionEqualToValue={(o, v) => o.id === v.id}
            value={selectedJob}
            onChange={(_, v) => {
              setSelectedJob(v)
              applyFilter('jobPositionId', v?.id ?? null)
            }}
            renderInput={params => <TextField {...params} label={t('Position')} />}
          />
        )}

        <Autocomplete
          sx={{ width: '21em' }}
          size="small"
          options={statusOptions}
          getOptionLabel={o => o.label}
          isOptionEqualToValue={(o, v) => o.value === v.value}
          value={status}
          disableClearable
          onChange={(_, v) => {
            setStatus(v)
            applyFilter('enrollment', v?.value ?? null)
          }}
          renderInput={params => (
            <TextField
              {...params}
              label={employmentType === 'student' ? t('Student Status') : t('Status')}
            />
          )}
        />
      </Stack>
      {!!searchLoading && <LinearProgress sx={{ position: 'absolute', bottom: 0, width: '100%' }} />}
    </Box>
  )
}

export default SearchBar
